import { HostObject } from "@amazon-sumerian-hosts/babylon";
import "@babylonjs/loaders/glTF";
import "./overlay.js";
import { Scene } from "@babylonjs/core/scene";
import { Color4 } from "@babylonjs/core/Maths/math";
import { DefaultRenderingPipeline } from "@babylonjs/core/PostProcesses/RenderPipeline/Pipelines/defaultRenderingPipeline";
import "@babylonjs/core/PostProcesses/RenderPipeline/postProcessRenderPipelineManagerSceneComponent";
import { ImageProcessingConfiguration } from "@babylonjs/core/Materials/imageProcessingConfiguration";
import DemoUtils from "./demo-utils.js";
import { cognitoIdentityPoolId, userPoolId, userPoolClientId, cognitoDomain, oauthRedirectUri, oauthIdentityProvider } from "./demo-credentials.js";
import { NovaSonicWebRTCClient } from "./webrtc-client.js";
import { AudioMouthSync } from "./audio-mouth-sync.js";
import { AvatarAnimation } from "./avatar-animation.js";
import { CognitoIdentityClient, GetIdCommand, GetCredentialsForIdentityCommand } from "@aws-sdk/client-cognito-identity";
import { AuthenticationDetails, CognitoUserPool, CognitoUser } from 'amazon-cognito-identity-js';

let host;
let scene;
let webrtcClient;
let mouthSync;
let anim;
let isConnected = false;
let usageTracker = null;
let idToken = null;

const KVS_CHANNEL_NAME = "worldcup-avatar-s2s";
const AWS_REGION = "us-east-1";
const COGNITO_IDP_PROVIDER = `cognito-idp.${AWS_REGION}.amazonaws.com/${userPoolId}`;

// Test-only: username/password (e.g. the 'pundit' account) via Cognito SRP.
const userPool = new CognitoUserPool({ UserPoolId: userPoolId, ClientId: userPoolClientId });

function signInWithPassword() {
  const username = (document.getElementById('floatingUsername') || {}).value || '';
  const password = (document.getElementById('floatingPassword') || {}).value || '';
  const cognitoUser = new CognitoUser({ Username: username, Pool: userPool });
  const authDetails = new AuthenticationDetails({ Username: username, Password: password });
  cognitoUser.authenticateUser(authDetails, {
    onSuccess: (result) => {
      idToken = result.getIdToken().getJwtToken();
      showUiScreen("chatbotUiScreen");
      if (typeof window.wcStart === "function") window.wcStart();
      updateStatus("Connecting…");
      connectWebRTC();
    },
    onFailure: (err) => alert(err.message || JSON.stringify(err)),
  });
}

// Redirect to the Cognito Hosted UI, which forwards to Federate -> Midway.
function loginWithMidway() {
  const params = new URLSearchParams({
    client_id: userPoolClientId,
    response_type: "code",
    scope: "openid profile email",
    redirect_uri: oauthRedirectUri,
    identity_provider: oauthIdentityProvider,
  });
  window.location.href = `https://${cognitoDomain}/oauth2/authorize?${params.toString()}`;
}

// Exchange the authorization code (from the Midway redirect) for tokens.
async function exchangeCodeForTokens(code) {
  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: userPoolClientId,
    code,
    redirect_uri: oauthRedirectUri,
  });
  const res = await fetch(`https://${cognitoDomain}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: body.toString(),
  });
  if (!res.ok) throw new Error("Token exchange failed: " + res.status);
  const tokens = await res.json();
  idToken = tokens.id_token;
  // Strip the ?code= from the URL so a refresh doesn't re-exchange.
  window.history.replaceState({}, document.title, oauthRedirectUri);
  return idToken;
}

// Exchange the authenticated id token for AWS credentials via the identity pool.
// Authenticated identities are NOT subject to the guest session policy that
// blocks Kinesis Video Streams, so these credentials can call KVS.
async function getCredentials() {
  if (!idToken) throw new Error("Not signed in");
  const logins = { [COGNITO_IDP_PROVIDER]: idToken };
  const cognitoClient = new CognitoIdentityClient({ region: AWS_REGION });
  const identityId = await cognitoClient.send(new GetIdCommand({ IdentityPoolId: cognitoIdentityPoolId, Logins: logins }));
  const creds = await cognitoClient.send(new GetCredentialsForIdentityCommand({ IdentityId: identityId.IdentityId, Logins: logins }));
  return {
    accessKeyId: creds.Credentials.AccessKeyId,
    secretAccessKey: creds.Credentials.SecretKey,
    sessionToken: creds.Credentials.SessionToken,
  };
}

async function createScene() {
  scene = new Scene();
  scene.clearColor = new Color4(0, 0, 0, 0);
  const { shadowGenerator } = DemoUtils.setupSceneEnvironment(scene);

  // Tier 1 visual polish
  scene.getEngine().setHardwareScalingLevel(1 / Math.min(window.devicePixelRatio || 1, 2));
  const pipeline = new DefaultRenderingPipeline("worldCupPipeline", true, scene);
  pipeline.samples = 4;
  pipeline.fxaaEnabled = true;
  pipeline.bloomEnabled = true;
  pipeline.bloomThreshold = 0.82;
  pipeline.bloomWeight = 0.22;
  pipeline.bloomScale = 0.5;
  pipeline.imageProcessingEnabled = true;
  pipeline.imageProcessing.toneMappingEnabled = true;
  pipeline.imageProcessing.toneMappingType = ImageProcessingConfiguration.TONEMAPPING_ACES;
  pipeline.imageProcessing.contrast = 1.1;
  pipeline.imageProcessing.exposure = 1.15;

  initUi();

  // AWS SDK for Polly (Sumerian Host still needs it for init, even if we don't use it for TTS)
  const region = cognitoIdentityPoolId.split(":")[0];
  AWS.config.region = region;
  AWS.config.credentials = new AWS.CognitoIdentityCredentials({ IdentityPoolId: cognitoIdentityPoolId });

  // Create Sumerian Host
  const characterId = "Luke";
  const pollyConfig = { pollyVoice: "Arthur", pollyEngine: "neural" };
  const characterConfig = HostObject.getCharacterConfig("./assets/character-assets", characterId);
  host = await HostObject.createHost(scene, characterConfig, pollyConfig);
  host.PointOfInterestFeature.setTarget(scene.activeCamera);
  anim = new AvatarAnimation(host);
  scene.meshes.forEach((mesh) => { shadowGenerator.addShadowCaster(mesh); });

  return scene;
}

function initUi() {
  const micBtn = document.getElementById("start-button");
  if (micBtn) micBtn.onclick = toggleMic;

  // Sign-in form: the button redirects to the Cognito Hosted UI -> Federate -> Midway.
  const signInForm = document.getElementById('sign-in-form');
  if (signInForm) {
    signInForm.addEventListener('submit', (event) => {
      event.preventDefault();
      event.stopPropagation();
      loginWithMidway();
    }, false);
  }

  // Test-only password sign-in (e.g. the 'pundit' account).
  const pwdBtn = document.getElementById('passwordSignInButton');
  if (pwdBtn) pwdBtn.onclick = (e) => { e.preventDefault(); signInWithPassword(); };

  // If we've returned from Midway with an auth code, complete sign-in.
  const code = new URLSearchParams(window.location.search).get('code');
  if (code) {
    completeSignIn(code);
  } else {
    showUiScreen("startScreen");
  }
}

async function completeSignIn(code) {
  showUiScreen("chatbotUiScreen");
  if (typeof window.wcStart === "function") window.wcStart();
  updateStatus("Signing in…");
  try {
    await exchangeCodeForTokens(code);
    updateStatus("Connecting…");
    // Nova Sonic greets automatically on connect (server-side trigger).
    connectWebRTC();
  } catch (err) {
    console.error("Sign-in failed:", err);
    updateStatus("Sign-in failed — reload to retry");
  }
}

// DemoHub usage tracker — initialise once after login. The user's email is
// decoded from the Cognito/Midway id token (works for both the password and
// Midway login paths; Midway tokens aren't in the Cognito-SDK localStorage keys
// the tracker auto-reads, so we pass the email explicitly). Config (apiUrl,
// demoId, terms/toast) comes from window.UsageTrackerConfig in usage-tracker-config.js.
function initUsageTracker() {
  if (usageTracker || typeof window.UsageTrackerAuto !== "function") return;
  let email;
  try {
    if (idToken) {
      const p = JSON.parse(atob(idToken.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
      // Only use a real email claim (Midway/Cognito). The test account has no
      // email, so we leave it undefined rather than send an invalid value.
      if (p.email && p.email.includes("@")) email = p.email;
    }
  } catch (e) { /* fall back to the tracker's own extraction */ }
  try {
    usageTracker = new window.UsageTrackerAuto({ frontendVersion: "1.0.0", userEmail: email });
  } catch (e) {
    console.warn("[usage-tracker] init failed", e);
  }
}

async function connectWebRTC() {
  if (isConnected) return;
  initUsageTracker();
  updateStatus("Connecting…");
  const micBtn = document.getElementById("start-button");
  if (micBtn) micBtn.classList.add("connecting");

  try {
    const credentials = await getCredentials();

    webrtcClient = new NovaSonicWebRTCClient({
      region: AWS_REGION,
      credentials,
      channelName: KVS_CHANNEL_NAME,
      onAudioTrack: handleRemoteAudio,
      onDataMessage: handleDataMessage,
    });

    await webrtcClient.connect();
    isConnected = true;
    updateStatus("🟢 Live — speak to Dave!");
    if (micBtn) { micBtn.classList.remove("connecting"); micBtn.classList.add("active"); }
    // Let the results-ticker (overlay.js) ask Dave about a clicked match.
    window.askDaveAboutMatch = (text) => webrtcClient.sendUserText(text);
  } catch (err) {
    console.error("WebRTC connection failed:", err);
    updateStatus("Connection failed — retry");
    isConnected = false;
  }
}

function handleRemoteAudio(remoteStream) {
  // Play the Nova Sonic audio via a dedicated <audio> element (most reliable
  // path for WebRTC remote audio; AudioContext alone can stay suspended).
  let audioEl = document.getElementById("novaSonicAudio");
  if (!audioEl) {
    audioEl = document.createElement("audio");
    audioEl.id = "novaSonicAudio";
    audioEl.autoplay = true;
    audioEl.playsInline = true;
    document.body.appendChild(audioEl);
  }
  audioEl.srcObject = remoteStream;
  audioEl.play().catch(err => console.warn("Audio play blocked:", err));

  // Drive the avatar's mouth from the same stream.
  mouthSync = new AudioMouthSync(host, {
    scene,
    onSpeechStart: () => { if (anim) anim.playTalkingGesture(); },
    onSpeechEnd: () => { if (typeof window.wcShowFixtures === "function") window.wcShowFixtures(); },
  });
  mouthSync.start(remoteStream);
}

function handleDataMessage(msg) {
  // Handle text output events from Nova Sonic (transcripts)
  if (msg.textOutput) {
    const text = msg.textOutput.content || "";
    if (text) {
      displayTranscript(text);
      reactToText(text);
    }
  }
}

// Trigger emotes/gestures based on what Sam says.
function reactToText(text) {
  if (!anim) return;
  const t = text.toLowerCase();
  // Celebratory moments: goals, wins, big results.
  if (/\b(goal|golazo|scores?|scored|wins?|won|champion|victor|brilliant|stunning|what a (strike|goal)|back of the net|top corner)\b/.test(t)) {
    anim.celebrate();
  } else if (t.trim().endsWith("?")) {
    // A considering beat when Sam poses a question.
    anim.think();
  }
}

function toggleMic() {
  if (!isConnected) {
    connectWebRTC();
    return;
  }
  const btn = document.getElementById("start-button");
  const muted = btn.classList.toggle("muted");
  webrtcClient.muteMic(muted);
  updateStatus(muted ? "🔇 Muted" : "🟢 Live — speak to Dave!");
}

function updateStatus(text) {
  const el = document.getElementById("transcriptText");
  if (el) el.innerText = text;
  // Always reveal the message container so status is visible.
  const container = document.getElementById("userMessageContainer");
  if (container) container.classList.add("showingMessage");
}

function displayTranscript(text) {
  const el = document.getElementById("transcriptText");
  if (el) el.innerText = `"${text}"`;
  const container = document.getElementById("userMessageContainer");
  if (container) container.classList.add("showingMessage");
}

function showUiScreen(id) {
  document.querySelectorAll("#uiScreens .screen").forEach((element) => {
    element.classList.toggle("hide", element.id !== id);
  });
}

DemoUtils.loadDemo(createScene);
